import { observable, action, computed } from 'mobx';
import sortBy from 'lodash/sortBy';
import { MINIMUM_COURSE_COMPLETION_PERCENTAGE } from 'config/constants';
import { extractFromSlug } from 'helpers/utils';
import {
  EntryProgressPair,
  ProgressByCourse,
  UserProgressPage,
} from 'types/progress';
import {
  CatalogEntry,
  CourseType,
  DeliveryFormat,
  LegacyEnrollment,
  LegacyEnrollmentEtype,
  OfferingVisibility,
} from 'types';
import { DateTime } from 'luxon';
import {
  getProgress,
  setCourseProgress,
  setCourseProgressExternalSubscription,
  getReporteesProgress,
  updateVideoProgress,
  saveUserProgressPage,
  getUserProgressPageCanon,
} from '../services/ProgressService';
import CatalogStore from './Catalog';
import { buildGetDeliveryFormat } from './Catalog/config';
import type { IRootStore } from './IRootStore';

class ProgressStore {
  @observable progressDict: Record<string, ProgressByCourse> = {};

  @observable isLoading = true;

  @observable userProgressPage: UserProgressPage;

  rootStore: IRootStore;

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
  }

  @action async getProgress() {
    this.isLoading = true;
    try {
      const progress = await getProgress();
      this.rootStore.classesStore.progress = progress;
      this.progressDict = progress;
    } finally {
      this.isLoading = false;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  @action async getReporteesProgress(
    organizationId: string,
    users: string[],
    courseType: CourseType,
    signal?: AbortSignal,
  ) {
    const progressData = await getReporteesProgress(
      organizationId,
      users,
      courseType,
      signal,
    );
    return progressData;
  }

  @computed get lastAccessProgress() {
    const sortedProgress = sortBy(
      Object.values(this.progressDict),
      '@timestamp',
    ).reverse();

    return sortedProgress?.length > 0 ? sortedProgress[0] : {};
  }

  @computed get coursesInProgress() {
    const courseEntries = Object.entries(this.progressDict);
    if (courseEntries.length) {
      return courseEntries.map(([, progress]) => progress);
    }
    return [];
  }

  @computed get progressByCourse(): ProgressByCourse[] {
    const progressDictByCourseCode = Object.entries(this.progressDict).reduce(
      (dict, [slug, progress]) => {
        const { code: courseCode } = extractFromSlug(slug);
        if (
          !dict[courseCode] ||
          progress['@timestamp'] > dict[courseCode]['@timestamp']
        ) {
          // eslint-disable-next-line no-param-reassign
          dict[courseCode] = progress;
        }
        return dict;
      },
      {},
    );

    const progressArray: ProgressByCourse[] = Object.values(
      progressDictByCourseCode,
    );

    return sortBy(progressArray, '@timestamp').reverse();
  }

  @action getProgressForCourse(
    courseSlug: string,
  ): ProgressByCourse | undefined {
    return this.progressByCourse.find(
      (progress) => progress?.course_slug === courseSlug,
    );
  }

  /**
   * Pairs progress records and à la carte enrollments with catalog entries.
   * Dedupes by course slug; progress entries take precedence over enrollments.
   *
   * NOTE: Relies on `progressByCourse` order (most recently updated first).
   * Uses a Map to deduplicate by course slug while preserving insertion order.
   */
  buildEntryProgressPairs(
    progressByCourse: ProgressByCourse[],
    alacarteEnrollments: LegacyEnrollment[],
  ): EntryProgressPair[] {
    const { catalogStore } = this.rootStore;
    const pairsMap = new Map<string, EntryProgressPair>();

    // 1. Add progress entries first (they take precedence)
    progressByCourse.forEach((progressRecord) => {
      const { code, version } = extractFromSlug(progressRecord.course_slug);
      const entry = catalogStore.getCatalogEntryForCourse(code, version, true);
      if (!entry) return;
      pairsMap.set(progressRecord.course_slug, {
        progress: progressRecord,
        entry,
      });
    });

    // 2. Some types of alacarte enrollments should be included even if they have no progress.
    const alacarteEnrollmentsToInclude = [
      LegacyEnrollmentEtype.Open,
      LegacyEnrollmentEtype.External,
      LegacyEnrollmentEtype.RHLSCourse,
    ];

    alacarteEnrollments.forEach((enrollment) => {
      if (!alacarteEnrollmentsToInclude.includes(enrollment.etype)) {
        return;
      }
      const slug = enrollment.slug_tag || enrollment.slug;
      if (!slug || pairsMap.has(slug)) return;
      const { code, version } = extractFromSlug(slug);
      const entry = catalogStore.getCatalogEntryForCourse(code, version, true);
      if (!entry) return;
      pairsMap.set(slug, { entry });
    });

    return Array.from(pairsMap.values());
  }

  @action clearProgress() {
    this.progressDict = {};
  }

  @action setCourseProgress = async (courseSlug: string, pageUUID?: string) => {
    const progress: { rv?: ProgressByCourse } = await setCourseProgress(
      courseSlug,
      pageUUID,
    );

    this.progressDict[courseSlug] = progress.rv;
    if (this.rootStore.classesStore.progress[courseSlug] && progress.rv) {
      this.rootStore.classesStore.progress[courseSlug].total_progress =
        progress.rv.total_progress;
      this.rootStore.classesStore.progress[courseSlug].total_score =
        progress.rv.total_score;
    }
    return progress;
  };

  @action updateVideoProgress = async (
    courseSlug: string,
    progress: any,
    currentVideo: string,
    timestamp: number,
  ) => {
    try {
      const progressDocument: ProgressByCourse = await updateVideoProgress(
        courseSlug,
        progress,
        currentVideo,
        timestamp,
      );
      this.progressDict[courseSlug] = progressDocument;
      if (
        this.rootStore.classesStore.progress[courseSlug] &&
        progressDocument
      ) {
        this.rootStore.classesStore.progress[courseSlug].total_progress =
          progressDocument.total_progress;
        this.rootStore.classesStore.progress[courseSlug].total_score =
          progressDocument.total_score;
      }
    } catch (err) {
      console.error(
        'Failed to update the progress of this video or playlist:',
        err,
      );
    }
  };

  // eslint-disable-next-line class-methods-use-this
  @action updateVideoProgressInPage = async (
    docId: string,
    currentVideo: string,
    timestamp: number,
  ) => {
    try {
      const data = {
        last_watched_video: currentVideo,
        last_watched_timestamp: timestamp,
      };
      await saveUserProgressPage(data, docId);
    } catch (err) {
      console.error(
        'Failed to update the progress of this video or playlist in page:',
        err,
      );
    }
  };

  @action getUserProgressPage = async (
    contentUUID: string,
    courseSlug: string,
    username: string,
  ): Promise<UserProgressPage> => {
    try {
      const userProgressPage = await getUserProgressPageCanon(
        contentUUID,
        courseSlug,
        username,
      );
      if (userProgressPage) {
        this.userProgressPage = userProgressPage;
      }
      return userProgressPage;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  // eslint-disable-next-line class-methods-use-this
  @action didJustEnrolledInCourse = (courseProgress: ProgressByCourse) => {
    const { thresholds } = courseProgress;
    const timestamp = courseProgress['@timestamp'];
    if (thresholds) {
      const isJustEnrolled = thresholds.some(
        (threshold) =>
          threshold.level === 'start' && timestamp === threshold.when,
      );

      return isJustEnrolled;
    }

    return false;
  };

  // eslint-disable-next-line class-methods-use-this
  @action didJustFinishedCourse = (courseProgress: ProgressByCourse) => {
    const { thresholds } = courseProgress;
    const timestamp = courseProgress['@timestamp'];
    if (!thresholds) {
      return false;
    }
    const isFinished = thresholds.some(
      (threshold) =>
        threshold.level === 'three_quarter' && timestamp === threshold.when,
    );

    return isFinished;
  };

  // eslint-disable-next-line class-methods-use-this
  @action setCourseProgressExternalSubscription = async (
    courseCode: string,
    currentProgress: unknown,
    subscription: string,
  ) => {
    try {
      const progress = await setCourseProgressExternalSubscription(
        courseCode,
        currentProgress,
        subscription,
      );

      return progress;
    } catch (err) {
      console.error(err);
      throw new Error(
        err.response?.data?.error ||
          `Couldn't update progress for external subscriber`,
      );
    }
  };

  // eslint-disable-next-line class-methods-use-this
  @action isChapterEnabled = (tag: string) =>
    ![1, 2].includes(parseInt(tag.replace('ch', ''), 10));

  shouldShowLegacyCertificateOfAttendance(
    progress = 0,
    progressCompletionTime = null,
  ) {
    if (progress < MINIMUM_COURSE_COMPLETION_PERCENTAGE) {
      return false;
    }

    const CredlyBadgeReleaseDate =
      this.rootStore.configStore.config.CREDLY_BADGE_RELEASE_DATE;
    if (!CredlyBadgeReleaseDate) {
      return true;
    }

    if (!progressCompletionTime) {
      return false;
    }

    const courseCompletionTimeUtc = DateTime.fromISO(progressCompletionTime, {
      zone: 'utc',
    });
    const CredlyBadgeReleaseDateUtc = DateTime.fromISO(CredlyBadgeReleaseDate, {
      zone: 'utc',
    });

    if (courseCompletionTimeUtc >= CredlyBadgeReleaseDateUtc) {
      return false;
    }

    return true;
  }

  @computed get isLoadingLastAccessed(): boolean {
    return !this.rootStore.catalogStore?.loaded || this.isLoading;
  }

  /**
   * A list of user's offerings paired with their progress data, used
   * to populate the "My Progress" widget on the dashboard.
   *
   * The result combines two data sources:
   * 1. Offerings with progress records (from `progressByCourse`)
   * 2. À la carte enrollments (Open, External, RHLSCourse) that may not have progress yet
   *
   * Filtering:
   * - Only includes offerings with `visibility: "visible"` (excludes hidden and upgrade-locked)
   * - Only includes offerings that exist in the catalog
   *
   * Sorting:
   * - Ordered by most recently updated first (based on progress `@timestamp`)
   * - À la carte enrollments without progress appear after items with progress
   */
  @computed get visibleOfferingsWithProgress(): (EntryProgressPair & {
    entry: CatalogEntry;
    deliveryFormat?: DeliveryFormat;
  })[] {
    // Check if progress data exists
    if (!this.progressByCourse) {
      return [];
    }

    // NOTE: We are relying on the order of this.progressByCourse being correct
    // (most recently updated first) for the next computed property.
    // Using a Map to deduplicate by course slug while preserving insertion order.
    const pairs = this.buildEntryProgressPairs(
      this.progressByCourse,
      this.rootStore.classesStore.standard,
    );

    const { catalogStore } = this.rootStore;

    // Ignoring entries with visibility Upgrade, and offerings that belong
    // in Saved for later (pin newer than last access / no progress yet).
    return pairs
      .filter(
        (p): p is EntryProgressPair & { entry: CatalogEntry } =>
          p.entry?.visibility === OfferingVisibility.Visible &&
          !catalogStore.isSavedForLater(p.entry.code),
      )
      .map((p) => ({
        progress: p.progress ?? null,
        entry: p.entry,
        deliveryFormat: buildGetDeliveryFormat(CatalogStore.isCompReview)(
          p.entry,
        ),
      })) as (EntryProgressPair & {
      entry: CatalogEntry;
      deliveryFormat?: DeliveryFormat;
    })[];
  }

  /**
   * Returns the last accessed offering from the visible offerings with progress.
   * Fallbacks to the last enrolled offering
   */
  @computed get lastAccessedOffering(): EntryProgressPair & {
    deliveryFormat?: DeliveryFormat;
  } {
    const visibleOfferings = this.visibleOfferingsWithProgress;

    if (visibleOfferings.length === 0) {
      return {};
    }

    // 2. Since the list is already ordered time,
    // the first element is the most recent visible offering.
    return visibleOfferings[0];
  }
}

export default ProgressStore;
